import { Inject, Injectable, Type } from '@angular/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { plainToInstance } from 'class-transformer';
import { Store } from '@ngrx/store';
import { setPathInfos } from '@madeinlune/ngrx-drupal-paths-to-routes';
import { ComponentStore } from '@ngrx/component-store';
import { sendRequest, upsertDrupalDatas } from './+state/drupal-data.actions';
import { JsonApiQuery } from './json-api.query';
import { headers, JsonApiResource, JsonApiResult } from './json-api.types';
import {
  DATA_ACCESS_BASE_URL,
  JSON_API_LANGUAGE,
  MODEL_CLASS_MAP,
} from '@nx-icilalune/data/access/tokens';
import { AppPathInfos } from '@nx-icilalune/drupal-types';

class JsonApiLoaderState {
  lang?: string;
}

export interface JsonApiLoaderResponse<T> {
  data?: T;
  next?: string | undefined;
}

@Injectable({
  providedIn: 'any',
})
export class JsonApiLoaderService extends ComponentStore<JsonApiLoaderState> {
  readonly lang$ = this.select((s) => s.lang);

  constructor(
    @Inject(DATA_ACCESS_BASE_URL) private baseUrl: string,
    @Inject(MODEL_CLASS_MAP) private classMap: Map<string, Type<any>>,
    @Inject(JSON_API_LANGUAGE) private activeLanguage$: Observable<string>,
    private http: HttpClient,
    private store: Store<any>
  ) {
    super({});
    this.patchState(
      this.activeLanguage$.pipe(
        map((lang) => {
          return {
            lang,
          };
        })
      )
    );
  }

  load(query: JsonApiQuery) {
    this.store.dispatch(sendRequest({ request: query }));
  }

  loadForReal<T>(query: JsonApiQuery): Observable<JsonApiLoaderResponse<T>> {
    const jsonApiUrl: URL = new URL(this.baseUrl);
    /*const type =
      query.type.indexOf('--') > -1
        ? query.type.split('--').join('/')
        : query.type;*/
    const type = query.type;
    jsonApiUrl.pathname = `/${this.get((s) => s.lang)}/api/${type}`;
    if (query.id) {
      jsonApiUrl.pathname = jsonApiUrl.pathname + `/${query.id}`;
    }
    if (query.include) {
      jsonApiUrl.searchParams.append('include', query.include.join(','));
    }
    if (query.filters) {
      for (const [key, value] of Object.entries(query.filters)) {
        jsonApiUrl.searchParams.append(`filter[${key}]`, value);
      }
    }
    if (query.page) {
      if (query.page?.limit) {
        jsonApiUrl.searchParams.append(
          `page[limit]`,
          query.page.limit.toString()
        );
      }
      if (typeof query.page?.offset === 'number') {
        jsonApiUrl.searchParams.append(
          `page[offset]`,
          query.page.offset.toString()
        );
      }
    }
    if (query.fields) {
      Object.keys(query.fields).forEach((key) => {
        if (query.fields?.[key]) {
          jsonApiUrl.searchParams.append(
            `fields[${key}]`,
            query.fields[key].join(',')
          );
        }
      });
    }
    if (query.sort) {
      query.sort.forEach((sort) => {
        jsonApiUrl.searchParams.append(
          `sort[sort-${sort.field}][path]`,
          sort.field
        );
        if (sort.direction === 'DESC') {
          jsonApiUrl.searchParams.append(
            `sort[sort-${sort.field}][direction]`,
            'DESC'
          );
        }
      });
    }

    const lang = this.get((s) => s.lang);

    return this.http
      .get<JsonApiResult>(decodeURI(jsonApiUrl.toString()), { headers })
      .pipe(
        map((response) => {
          const data: JsonApiResource[] = Array.isArray(response.data)
            ? response.data
            : [response.data];
          const included: JsonApiResource[] = response.included ?? [];
          const parsedDataAndIncluded = [...data, ...included]
            .map((d) => {
              const dataModelClass = this.classMap.get(d.type);
              //if needed, we want to flatten a relation property value into the parnt objet, to simplify data management
              if (query.relationAttributeEnhancers) {
                this.#manageEnhancer(
                  query.relationAttributeEnhancers,
                  d,
                  included
                );
              }
              if (d.type === 'svgs') {
                const fileResource: JsonApiResource | undefined = included.find(
                  (i) => {
                    return d.relationships.fileRef?.data?.id === i.id;
                  }
                );
                if (fileResource) {
                  d.relationships.fileRef.data.meta = {
                    ...d.relationships.fileRef.data.meta,
                    url: `${this.baseUrl}${fileResource.attributes.uri.url}`,
                  };
                }
              }
              if (!dataModelClass) {
                // console.warn('no data model class for this type', d.type);
              }
              if (d.attributes['langcode']) {
                d.attributes['langcode'] = lang;
              }
              if (dataModelClass) {
                return plainToInstance(dataModelClass, d, {
                  excludeExtraneousValues: true,
                  exposeUnsetFields: false,
                });
              }
              return null;
            })
            .filter((d) => !!d);
          return {
            data: parsedDataAndIncluded,
            next: response.links?.next?.href,
          };
        }),
        tap((jsonApiLoaderResponse: any) => {
          this.store.dispatch(
            upsertDrupalDatas({ drupalDatas: jsonApiLoaderResponse.data })
          );
        }),
        tap((jsonApiLoaderResponse: any) => {
          const data = jsonApiLoaderResponse.data;
          const pathInfos: AppPathInfos[] = data
            .filter(
              (r) =>
                !!(r as any).path && (r as any).type.indexOf('term--') === -1
            )
            .map((resource) => {
              if (resource.type === 'menuLinks') {
                const nodeValue = resource.meta.nodeValue;
                const mainMenuItem: any = resource;
                if (nodeValue) {
                  const appInfos: AppPathInfos = {
                    type:
                      mainMenuItem?.linkNode?.type ||
                      (nodeValue.type === 'page' ? 'pages' : nodeValue.type),
                    id: mainMenuItem?.linkNode?.id || nodeValue.uuid,
                    canonicalUrl: `${mainMenuItem.path}`,
                    path: `${mainMenuItem.path}`,
                    language: mainMenuItem.language,
                    frontPage: false,
                    title: '',
                    description: '',
                    meta: resource.meta,
                  };
                  return appInfos;
                }
                return null;
              }
              return plainToInstance(AppPathInfos, resource, {
                excludeExtraneousValues: true,
                exposeUnsetFields: false,
              });
            })
            .filter((p) => !!p);
          if (pathInfos.length > 0) {
            this.store.dispatch(setPathInfos({ pathInfos }));
          }
        }),
        map((jsonApiLoaderResponse: any) => {
          const data = jsonApiLoaderResponse.data;
          let resultData: T;
          if (!query.id) {
            resultData = data.filter(
              (d) => d.type === query.type
            ) as unknown as T;
          } else {
            resultData = data.find((d) => d.id === query.id);
          }
          return { data: resultData, next: jsonApiLoaderResponse.next };
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  #manageEnhancer(
    enhancers: string[],
    resource: JsonApiResource,
    included: JsonApiResource[]
  ) {
    enhancers.forEach((propertyPath) => {
      const property = propertyPath.split('.').shift();
      const relationProperty = propertyPath.split('.').pop();
      if (property && relationProperty) {
        const dataRelation = resource.relationships?.[property]?.data;
        if (dataRelation) {
          const includedResource = included.find(
            (ir) => ir.id === dataRelation.id
          );
          if (includedResource) {
            if (includedResource.attributes?.[relationProperty]) {
              dataRelation.meta = {
                ...dataRelation.meta,
                [relationProperty]:
                  includedResource.attributes?.[relationProperty],
              };
            } else if (includedResource.relationships?.[relationProperty]) {
              dataRelation.meta = {
                ...dataRelation.meta,
                [relationProperty]:
                  includedResource.relationships?.[relationProperty],
              };
            }
          }
        }
      }
    });
  }
}
